import { ImageIcon } from 'lucide-react'

import * as React from 'react'

import { cn } from '@/lib/utils'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Skeleton } from '@/components/ui/skeleton'

export interface AdvancedImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  /**
   * Optional fallback image URL to show if the main src fails.
   */
  fallbackSrc?: string
  /**
   * Text to display when image fails and no fallbackSrc is provided (or if it also fails).
   */
  fallbackText?: string
  /**
   * If true, shows a skeleton loader while loading.
   * @default true
   */
  showSkeleton?: boolean
  /**
   * If true, treats the 'src' prop as an Unsplash photo ID and constructs the URL automatically.
   */
  useUnsplash?: boolean
  width?: number | string
  height?: number | string
  /**
   * If true, renders the image as an Avatar.
   */
  avatar?: boolean
  /**
   * Optional className for the AvatarFallback.
   */
  fallbackClassName?: string
}

const constructUnsplashUrl = (id: string, w: number | string = 800, h?: number | string) => {
  const widthParam = typeof w === 'number' ? w : 800
  const heightParam = typeof h === 'number' ? `&h=${h}` : ''

  if (id === 'random') {
    return `https://picsum.photos/${widthParam}/${h || widthParam}`
  }

  return `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=${widthParam}&q=80${heightParam}`
}

export const AdvancedImage = React.forwardRef<HTMLImageElement, AdvancedImageProps>(
  (
    {
      className,
      src,
      alt,
      fallbackSrc,
      fallbackText,
      showSkeleton = true,
      width,
      height,
      useUnsplash,
      avatar,
      fallbackClassName,
      onLoad,
      onError,
      ...props
    },
    ref
  ) => {
    const [status, setStatus] = React.useState<'loading' | 'loaded' | 'error'>('loading')

    const finalSrc = React.useMemo(() => {
      if (useUnsplash && src) return constructUnsplashUrl(src, width, height)
      return src
    }, [useUnsplash, src, width, height])

    const [imgSrc, setImgSrc] = React.useState<string | undefined>(finalSrc)

    const style = {
      width: typeof width === 'number' ? `${width}px` : width,
      height: typeof height === 'number' ? `${height}px` : height,
    }

    React.useEffect(() => {
      setImgSrc(finalSrc)
      setStatus('loading')
    }, [finalSrc])

    const handleLoad = React.useCallback(
      (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
        setStatus('loaded')
        onLoad?.(e)
      },
      [onLoad]
    )

    const handleError = React.useCallback(
      (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
        if (imgSrc === finalSrc && fallbackSrc) {
          setImgSrc(fallbackSrc)
        } else {
          setStatus('error')
        }
        onError?.(e)
      },
      [onError, imgSrc, finalSrc, fallbackSrc]
    )

    if (avatar) {
      return (
        <Avatar className={cn(className)} style={{ width: style.width, height: style.height }}>
          <AvatarImage
            src={imgSrc}
            alt={alt}
            className={cn(
              'object-cover',
              status === 'loading' && showSkeleton ? 'opacity-0' : 'opacity-100'
            )}
            onLoadingStatusChange={(status) => {
              if (status === 'loading') setStatus('loading')
              else if (status === 'loaded') {
                // Radix handles loaded
                setStatus('loaded')
              } else if (status === 'error') {
                // Radix detected error
                // We can trigger our handleError logic if we want fallbackSrc support
                // But Radix AvatarImage automatically hides on error.
                // If we want to support fallbackSrc, standard onError on the img tag (passed via AvatarImage) works better
              }
            }}
            onError={handleError}
            onLoad={handleLoad}
            {...props}
          />
          <AvatarFallback
            className={cn(
              'bg-subdued text-micro text-muted-foreground',
              fallbackClassName
            )}
          >
            {showSkeleton && status === 'loading' ? (
              <Skeleton className="h-full w-full" />
            ) : (
              fallbackText
            )}
          </AvatarFallback>
        </Avatar>
      )
    }

    return (
      <div className={cn('relative overflow-hidden bg-muted/10', className)} style={style}>
        {showSkeleton && status === 'loading' && imgSrc && (
          <Skeleton className="absolute inset-0 h-full w-full" />
        )}

        {(status === 'error' || !imgSrc) && (
          <div className="flex h-full w-full flex-col items-center justify-center bg-muted p-2 text-muted-foreground">
            <ImageIcon className="h-8 w-8 opacity-50 mb-1" />
            {fallbackText && (
              <span className="text-xs font-medium text-center">{fallbackText}</span>
            )}
          </div>
        )}

        {/* No source: the fallback above is shown instead of an <img src=""> (which refetches the page). */}
        {imgSrc && (
          <img
            ref={ref}
            src={imgSrc}
            alt={alt}
            width={width}
            height={height}
            className={cn(
              'h-full w-full object-cover transition-opacity duration-500',
              status === 'loading' ? 'opacity-0' : 'opacity-100',
              status === 'error' ? 'hidden' : ''
            )}
            onLoad={handleLoad}
            onError={handleError}
            {...props}
          />
        )}
      </div>
    )
  }
)
AdvancedImage.displayName = 'AdvancedImage'
